import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { serviceDetails, serviceImages } from '../utils/serviceDetails';
import styles from './ServicePage.module.css';

const ServicePage = ({ service }) => {
  const details = serviceDetails[service];
  const serviceImage = serviceImages[service] || '/img/default.png';

  // Fallback for services without detailed information
  if (!details) {
    return (
      <>
        <Header />
        <div className={styles.servicePage}>
          <div className="container">
            <div className={styles.serviceHeader}>
              <div className={styles.serviceImageContainer}>
                <img
                  src={serviceImage}
                  alt={service}
                  className={styles.serviceImage}
                  onError={(e) => {
                    if (e.target.src !== '/img/default.png') {
                      e.target.src = '/img/default.png';
                    } else {
                      e.target.style.display = 'none';
                      e.target.parentElement.style.display = 'none';
                    }
                  }}
                  loading="lazy"
                />
              </div>
              <h1 className={styles.serviceTitle}>{service}</h1>
              <p className={styles.serviceSubtitle}>
                Professional {service} services with expert guidance
              </p>
            </div>

            <div className={styles.serviceContent}>
              <div className={styles.serviceInfo}>
                <h2>About {service}</h2>
                <p>
                  Get comprehensive {service} services from our team of experienced professionals.
                  We provide end-to-end support to ensure a smooth and hassle-free process.
                </p>

                <div className={styles.ctaSection}>
                  <a href="tel:+919552319748" className={styles.ctaButton}>
                    <i className="fas fa-phone"></i>
                    Contact Us Now
                  </a>
                  <Link to="/" className={styles.secondaryButton}>
                    Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className={styles.servicePage}>
        <div className="container">
          {/* Service Header */}
          <div className={styles.serviceHeader}>
            <div className={styles.serviceImageContainer}>
              <img
                src={serviceImage}
                alt={service}
                className={styles.serviceImage}
                onError={(e) => {
                  if (e.target.src !== '/img/default.png') {
                    e.target.src = '/img/default.png';
                  } else {
                    e.target.style.display = 'none';
                    e.target.parentElement.style.display = 'none';
                  }
                }}
                loading="lazy"
              />
            </div>
            <h1 className={styles.serviceTitle}>{service}</h1>
            <p className={styles.serviceSubtitle}>
              Professional {service} services with expert guidance and support
            </p>
          </div>

          <div className={styles.serviceContent}>
            {/* Pricing Section (if available) */}
            {details.pricing && (
              <div className={styles.pricingSection}>
                <h2 className={styles.sectionTitle}>
                  <i className="fas fa-tag"></i> Pricing & Timeline
                </h2>
                <div className={styles.pricingCard}>
                  <div className={styles.priceHeader}>
                    <span className={styles.currentPrice}>{details.pricing.standard || details.pricing.total}</span>
                    {details.pricing.original && (
                      <span className={styles.originalPrice}>{details.pricing.original}</span>
                    )}
                  </div>
                  <div className={styles.timeline}>
                    <i className="fas fa-clock"></i>
                    <strong>Timeline:</strong> {details.pricing.timeline}
                  </div>
                  {details.pricing.includes && (
                    <div className={styles.includes}>
                      <h3>What's Included:</h3>
                      <ul>
                        {details.pricing.includes.map((item, index) => (
                          <li key={index}>
                            <i className="fas fa-check"></i>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {details.pricing.govtFee && (
                    <div className={styles.govtFee}>
                      <strong>Government Fee:</strong> {details.pricing.govtFee}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Overview Section */}
            <div className={styles.overviewSection}>
              <h2 className={styles.sectionTitle}>Overview</h2>
              <p className={styles.description}>{details.description}</p>
            </div>

            {/* Threshold Limits (for GST) */}
            {details.thresholdLimits && (
              <div className={styles.thresholdSection}>
                <h2 className={styles.sectionTitle}>
                  <i className="fas fa-info-circle"></i> {details.thresholdLimits.title}
                </h2>
                <div className={styles.thresholdGrid}>
                  {details.thresholdLimits.categories.map((category, index) => (
                    <div key={index} className={styles.thresholdCard}>
                      <h3>{category.type}</h3>
                      <p>{category.limit}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trademark Classes */}
            {details.trademarkClasses && (
              <div className={styles.classesSection}>
                <h2 className={styles.sectionTitle}>
                  <i className="fas fa-list"></i> Trademark Classes
                </h2>
                <div className={styles.classesInfo}>
                  <p><strong>Total Classes:</strong> {details.trademarkClasses.total}</p>
                  <p><strong>Goods:</strong> {details.trademarkClasses.goodsClasses}</p>
                  <p><strong>Services:</strong> {details.trademarkClasses.servicesClasses}</p>
                  <p><strong>Classification:</strong> {details.trademarkClasses.classification}</p>
                  <p className={styles.note}>{details.trademarkClasses.note}</p>
                </div>
              </div>
            )}

            {/* Benefits Section */}
            <div className={styles.benefitsSection}>
              <h2 className={styles.sectionTitle}>
                <i className="fas fa-star"></i> Key Benefits
              </h2>
              <div className={styles.benefitsGrid}>
                {details.benefits.map((benefit, index) => (
                  <div key={index} className={styles.benefitCard}>
                    <i className="fas fa-check-circle"></i>
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Documents Required */}
            <div className={styles.documentsSection}>
              <h2 className={styles.sectionTitle}>
                <i className="fas fa-file-alt"></i> Documents Required
              </h2>

              {/* Categorized documents (for Pvt Ltd, Trademark) */}
              {details.documentsRequired && (
                <div className={styles.documentCategories}>
                  {details.documentsRequired.indianNationals && (
                    <div className={styles.docCategory}>
                      <h3>For Indian Nationals:</h3>
                      <ul>
                        {details.documentsRequired.indianNationals.map((doc, index) => (
                          <li key={index}>
                            <i className="fas fa-folder"></i>
                            {doc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {details.documentsRequired.foreignNationals && (
                    <div className={styles.docCategory}>
                      <h3>For Foreign Nationals:</h3>
                      <ul>
                        {details.documentsRequired.foreignNationals.map((doc, index) => (
                          <li key={index}>
                            <i className="fas fa-folder"></i>
                            {doc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {details.documentsRequired.registeredOffice && (
                    <div className={styles.docCategory}>
                      <h3>For Registered Office:</h3>
                      <ul>
                        {details.documentsRequired.registeredOffice.map((doc, index) => (
                          <li key={index}>
                            <i className="fas fa-folder"></i>
                            {doc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {details.documentsRequired.individuals && (
                    <div className={styles.docCategory}>
                      <h3>For Individuals/Sole Proprietors:</h3>
                      <ul>
                        {details.documentsRequired.individuals.map((doc, index) => (
                          <li key={index}>
                            <i className="fas fa-folder"></i>
                            {doc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {details.documentsRequired.partnershipLlpCompany && (
                    <div className={styles.docCategory}>
                      <h3>For Partnership/LLP/Company:</h3>
                      <ul>
                        {details.documentsRequired.partnershipLlpCompany.map((doc, index) => (
                          <li key={index}>
                            <i className="fas fa-folder"></i>
                            {doc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Standard document list */}
              <ul className={styles.documentList}>
                {details.documents.map((doc, index) => (
                  <li key={index}>
                    <i className="fas fa-folder"></i>
                    {doc}
                  </li>
                ))}
              </ul>
            </div>

            {/* Process Timeline */}
            <div className={styles.processSection}>
              <h2 className={styles.sectionTitle}>
                <i className="fas fa-tasks"></i> Our Process
              </h2>
              <div className={styles.timeline}>
                {details.process.map((step, index) => (
                  <div key={index} className={styles.timelineItem}>
                    <div className={styles.timelineNumber}>{index + 1}</div>
                    <div className={styles.timelineContent}>
                      <p>{step}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.timelineInfo}>
                <i className="fas fa-clock"></i>
                <strong>Expected Timeline:</strong> {details.timeline}
              </div>
            </div>

            {/* Common Mistakes (if available) */}
            {details.commonMistakes && (
              <div className={styles.mistakesSection}>
                <h2 className={styles.sectionTitle}>
                  <i className="fas fa-exclamation-triangle"></i> Common Mistakes to Avoid
                </h2>
                <ul className={styles.mistakesList}>
                  {details.commonMistakes.map((mistake, index) => (
                    <li key={index}>
                      <i className="fas fa-times-circle"></i>
                      {mistake}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Trademark Types (if available) */}
            {details.trademarkTypes && (
              <div className={styles.typesSection}>
                <h2 className={styles.sectionTitle}>
                  <i className="fas fa-th-large"></i> Types of Trademarks
                </h2>
                <div className={styles.typesGrid}>
                  {details.trademarkTypes.map((type, index) => (
                    <div key={index} className={styles.typeCard}>
                      <p>{type}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FAQs Section */}
            <div className={styles.faqSection}>
              <h2 className={styles.sectionTitle}>
                <i className="fas fa-question-circle"></i> Frequently Asked Questions
              </h2>
              <div className={styles.faqList}>
                {details.faqs.map((faq, index) => (
                  <div key={index} className={styles.faqItem}>
                    <h3 className={styles.faqQuestion}>
                      <i className="fas fa-chevron-right"></i>
                      {faq.question}
                    </h3>
                    <p className={styles.faqAnswer}>{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className={styles.ctaSection}>
              <h2>Ready to Get Started?</h2>
              <p>Our expert team is here to assist you with {service}. Contact us now for a consultation!</p>
              <div className={styles.ctaButtons}>
                <a href="tel:+919552319748" className={styles.ctaButton}>
                  <i className="fas fa-phone"></i>
                  Call Us: +91 95523 19748
                </a>
                <a href="https://wa.me/919552319748" className={styles.whatsappButton} target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-whatsapp"></i>
                  WhatsApp Us
                </a>
                <Link to="/" className={styles.secondaryButton}>
                  <i className="fas fa-home"></i>
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ServicePage;
